import { IconStar } from '@components/icons/star';
import { IconStarSolid } from '@components/icons/star-solid';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { useAddFavorite, useCachedFavorites, useRemoveFavorite } from 'api/favorites/hooks';
import { FavoriteContentType } from 'api/favorites/requests';

const useFavoriteItem = ({ shortId, contentType }: FavoriteMenuItemProps) => {
  const cachedFavorites = useCachedFavorites();

  return cachedFavorites.find(
    (favoriteItem) =>
      favoriteItem.objectShortId === shortId && favoriteItem.contentType === contentType
  );
};

type FavoriteMenuItemProps = {
  shortId: string;
  contentType: FavoriteContentType;
};

export const FavoriteMenuItem = ({ contentType, shortId }: FavoriteMenuItemProps) => {
  const { t } = useScopedTranslate(locale);
  const { addToFavorites, favoriteItem } = useFavoriteItemActions({
    contentType,
    shortId,
  });

  return favoriteItem ? (
    <RemoveFavoriteMenuItem shortId={favoriteItem.shortId} />
  ) : (
    <Menu.Item
      onSelect={() => addToFavorites.mutate({ contentType, objectShortId: shortId })}
      dataCy="add-to-favorites"
      label={t('addToFavorites')}
      icon={<IconStar />}
    />
  );
};

export const RemoveFavoriteMenuItem = ({ shortId }: Pick<FavoriteMenuItemProps, 'shortId'>) => {
  const { t } = useScopedTranslate(locale);
  const removeFromFavorites = useRemoveFavorite();
  return (
    <Menu.Item
      onSelect={() => removeFromFavorites.mutate(shortId)}
      dataCy="remove-from-favorites"
      label={t('removeFromFavorites')}
      icon={<IconStarSolid color="orange100" />}
    />
  );
};

export const useFavoriteItemActions = ({ contentType, shortId }: FavoriteMenuItemProps) => {
  const favoriteItem = useFavoriteItem({ contentType, shortId });
  const removeFromFavorites = useRemoveFavorite();
  const addToFavorites = useAddFavorite();

  return {
    favoriteItem,
    removeFromFavorites,
    addToFavorites,
  };
};

export const locale = {
  en: {
    addToFavorites: 'Add to favourites',
    removeFromFavorites: 'Remove from favourites',
  },
};
